import { useEffect, useState } from 'react';
import { ApiError, api, type PostSummary, type PostVariant } from './api.js';
import { LIBELLES_RESEAU, formatOccupation, seuilOccupation, tauxOccupation } from './networks.js';

/** 09:00 demain reste dans la fenêtre 9h–12h fixée par les règles de publication. */
function creneauParDefaut(): string {
  const demain = new Date();
  demain.setDate(demain.getDate() + 1);
  demain.setHours(9, 0, 0, 0);
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${demain.getFullYear()}-${pad(demain.getMonth() + 1)}-${pad(demain.getDate())}T${pad(demain.getHours())}:${pad(demain.getMinutes())}`;
}

export function Review({ csrfToken }: { csrfToken: string | undefined }) {
  const [queue, setQueue] = useState<PostSummary[] | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  async function rafraichirFile() {
    try {
      const { posts } = await api.reviewQueue();
      setQueue(posts);
    } catch (error) {
      setErreur(error instanceof ApiError ? error.message : 'Chargement impossible.');
    }
  }

  useEffect(() => {
    void rafraichirFile();
  }, []);

  if (selectedId) {
    return <DetailPost postId={selectedId} csrfToken={csrfToken} onRetour={() => setSelectedId(null)} onTraite={() => { setSelectedId(null); void rafraichirFile(); }} />;
  }

  const total = queue?.length ?? 0;

  return (
    <section className="bloc validation-page">
      <div className="intro-page">
        <div><p className="surtitre">File de décision</p><h2>{total || 'Aucun'} post{total > 1 ? 's' : ''} à valider</h2></div>
        <p>Le visuel d’abord. Ouvrez ensuite un post pour relire chaque version réseau et choisir son créneau.</p>
      </div>

      {erreur ? <p className="erreur" role="alert">{erreur}</p> : null}
      {queue === null ? <p className="sous-titre">Chargement…</p> : null}
      {queue !== null && total === 0 ? <div className="vide"><span className="vide-icone">✓</span><p>Tout est relu.</p><span>Les prochains contenus générés arriveront ici avant toute diffusion.</span></div> : null}

      {queue !== null && total > 0 ? (
        <ul className="review-gallery">
          {queue.map((post) => (
            <li key={post.id}>
              <article className="review-card">
                <button type="button" className="review-visuel" onClick={() => setSelectedId(post.id)} aria-label={`Ouvrir ${post.topic.title}`}>
                  {post.mediaId ? post.mediaKind === 'video'
                    ? <video src={`/api/media/${post.mediaId}`} muted playsInline preload="metadata" aria-label={`Vidéo de ${post.topic.title}`} />
                    : <img src={`/api/media/${post.mediaId}`} alt={`Visuel de ${post.topic.title}`} />
                    : <span className="visuel-placeholder"><b>Aucun visuel</b><small>{post.visualError ? 'Composition à corriger' : 'Texte seul'}</small></span>}
                </button>
                <div className="review-card-contenu">
                  <div className="review-meta"><span>{post.category.name}</span><small>Créé le {new Date(post.createdAt).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })}</small></div>
                  <h3>{post.topic.title}</h3>
                  <button type="button" onClick={() => setSelectedId(post.id)}>Ouvrir l’éditeur</button>
                </div>
              </article>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}

function DetailPost({ postId, csrfToken, onRetour, onTraite }: { postId: string; csrfToken: string | undefined; onRetour: () => void; onTraite: () => void }) {
  const [post, setPost] = useState<PostSummary | null>(null);
  const [variants, setVariants] = useState<PostVariant[]>([]);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState<string | null>(null);
  const [action, setAction] = useState<'approve' | 'changes' | 'reject' | null>(null);
  const [creneau, setCreneau] = useState(creneauParDefaut());
  const [commentaire, setCommentaire] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [textesModifies, setTextesModifies] = useState(false);
  const [enregistrementTextes, setEnregistrementTextes] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    let vivant = true;
    api.postDetail(postId)
      .then(({ post: detail, variants: variantes }) => { if (vivant) { setPost(detail); setVariants(variantes); } })
      .catch((error: unknown) => setErreur(error instanceof ApiError ? error.message : 'Chargement impossible.'))
      .finally(() => vivant && setChargement(false));
    return () => { vivant = false; };
  }, [postId]);

  async function confirmer() {
    setEnvoi(true);
    setErreur(null);
    try {
      if (action === 'approve') await api.approvePost(postId, new Date(creneau).toISOString(), csrfToken);
      if (action === 'changes') {
        if (!commentaire.trim()) throw new ApiError('Indiquez ce qui doit changer.', 'COMMENT_REQUIRED', 400);
        await api.requestChanges(postId, commentaire, csrfToken);
      }
      if (action === 'reject') {
        if (!commentaire.trim()) throw new ApiError('Indiquez le motif du refus.', 'COMMENT_REQUIRED', 400);
        await api.rejectPost(postId, commentaire, csrfToken);
      }
      onTraite();
    } catch (error) {
      setErreur(error instanceof ApiError ? error.message : 'Action impossible.');
    } finally {
      setEnvoi(false);
    }
  }

  function updateVariant(id: string, patch: Partial<PostVariant>) {
    setVariants((current) => current.map((variant) => variant.id === id ? { ...variant, ...patch } : variant));
    setTextesModifies(true);
    setMessage(null);
  }

  async function enregistrerTextes() {
    setEnregistrementTextes(true); setErreur(null); setMessage(null);
    try {
      await api.updatePostVariants(postId, variants.map(({ id, body, hashtags }) => ({ id, body, hashtags })), csrfToken);
      setTextesModifies(false);
      setMessage('Les textes corrigés sont enregistrés.');
    } catch (error) {
      setErreur(error instanceof ApiError ? error.message : 'Enregistrement des textes impossible.');
    } finally {
      setEnregistrementTextes(false);
    }
  }

  if (chargement) return <section className="bloc"><p className="sous-titre">Chargement…</p></section>;
  if (!post) return <section className="bloc"><p className="erreur">Post introuvable.</p><button type="button" className="discret" onClick={onRetour}>Retour à la file</button></section>;

  return (
    <section className="bloc detail-post">
      <button type="button" className="retour" onClick={onRetour}>← Retour à la file</button>
      <div className="detail-en-tete"><div><span className="surtitre">{post.category.name}</span><h2>{post.topic.title}</h2></div><span className="badge-statut" data-statut="pending_review">À valider</span></div>

      <div className="editeur-grille">
        <aside className="editeur-visuel-panel">
          <div className="editeur-panneau-titre"><h3>Visuel</h3><span>Format publication</span></div>
          <div className="editeur-canevas">
            {post.mediaId ? (
              post.mediaKind === 'video'
                ? <video className="visuel-post" src={`/api/media/${post.mediaId}`} controls playsInline preload="metadata" aria-label={`Vidéo de ${post.topic.title}`} />
                : <img className="visuel-post" src={`/api/media/${post.mediaId}`} alt={`Visuel de ${post.topic.title}`} />
            ) : post.visualError ? (
              <div className="visuel-absent"><p className="visuel-absent-titre">Aucun visuel</p><p>{post.visualError}</p><p>Le texte reste publiable sur Facebook et LinkedIn. Instagram exigera une image.</p></div>
            ) : <div className="visuel-absent"><p className="visuel-absent-titre">Texte seul</p><p>Aucun média n’est rattaché à cette publication.</p></div>}
          </div>
        </aside>

        <div className="editeur-contenu">
          <section className="variantes-panel">
            <div className="editeur-panneau-titre"><h3>Textes par réseau</h3><span>{textesModifies ? 'Modifications non enregistrées' : `${variants.length} version${variants.length > 1 ? 's' : ''}`}</span></div>
            {variants.length === 0 ? <p className="variante-vide">Aucune variante générée pour ce post.</p> : variants.map((variant) => {
              const taux = tauxOccupation(variant.network, variant.body.length);
              return (
                <article key={variant.id} className="variante">
                  <div className="variante-entete"><span>{LIBELLES_RESEAU[variant.network] ?? variant.network}</span><small>{formatOccupation(variant.network, variant.body.length)}</small></div>
                  <div className="reglette" data-seuil={seuilOccupation(taux)}><span style={{ width: `${Math.max(taux * 100, 1)}%` }} /></div>
                  <label className="champ-texte-variante">Texte publié
                    <textarea rows={7} value={variant.body} onChange={(event) => updateVariant(variant.id, { body: event.target.value })} />
                  </label>
                  <label className="champ-texte-variante">Hashtags
                    <input value={variant.hashtags.map((hashtag) => `#${hashtag}`).join(' ')} placeholder="#immobilier #lotetgaronne" onChange={(event) => updateVariant(variant.id, {
                      hashtags: event.target.value.split(/[\s,]+/).map((hashtag) => hashtag.replace(/^#+/, '').trim()).filter(Boolean),
                    })} />
                  </label>
                </article>
              );
            })}
          </section>

          {erreur ? <p className="erreur" role="alert">{erreur}</p> : null}
          {message ? <p className="succes" role="status">{message}</p> : null}
          {textesModifies ? <button type="button" className="enregistrer-textes" disabled={enregistrementTextes} onClick={() => void enregistrerTextes()}>{enregistrementTextes ? 'Enregistrement…' : 'Enregistrer les textes corrigés'}</button> : null}
          {action === null ? (
            <div className="barre-decision"><button type="button" disabled={textesModifies} onClick={() => setAction('approve')}>Programmer</button><button type="button" disabled={textesModifies} className="discret" onClick={() => setAction('changes')}>Demander une retouche</button><button type="button" disabled={textesModifies} className="ecarter" onClick={() => setAction('reject')}>Écarter</button></div>
          ) : action === 'approve' ? (
            <div className="panneau-action"><label htmlFor="creneau">Diffusion prévue le</label><input id="creneau" type="datetime-local" value={creneau} onChange={(event) => setCreneau(event.target.value)} /><div className="actions-secondaires"><button type="button" onClick={() => void confirmer()} disabled={envoi}>{envoi ? 'Envoi…' : 'Programmer la publication'}</button><button type="button" className="discret" onClick={() => setAction(null)}>Annuler</button></div></div>
          ) : (
            <div className="panneau-action"><label htmlFor="commentaire">{action === 'changes' ? 'Ce qui doit changer' : 'Motif du refus'}</label><textarea id="commentaire" rows={3} value={commentaire} onChange={(event) => setCommentaire(event.target.value)} placeholder={action === 'changes' ? 'Trop long pour Instagram, ton trop formel…' : 'Sujet déjà traité le mois dernier…'} /><div className="actions-secondaires"><button type="button" onClick={() => void confirmer()} disabled={envoi}>{envoi ? 'Envoi…' : action === 'changes' ? 'Renvoyer en rédaction' : 'Écarter ce post'}</button><button type="button" className="discret" onClick={() => setAction(null)}>Annuler</button></div></div>
          )}
        </div>
      </div>
    </section>
  );
}
